import React, { useRef, useState } from 'react';
import { Upload, Download, Loader2 } from 'lucide-react';
import { api } from '../lib/api';

const plural = (n, w) => `${n} ${w}${n === 1 ? '' : 's'}`;
const TEMPLATE = 'Name,Designation,Organisation,Sector,Citation\n';

export default function BulkImport({ name, onDone }) {
  const ref = useRef(null);
  const [mode, setMode] = useState('append');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const downloadTemplate = () => {
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([TEMPLATE], { type: 'text/csv' }));
    a.download = 'the100-leaders-template.csv';
    a.click();
  };
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (mode === 'replace' && !window.confirm('Replace the whole list? All current leaders will be deleted first.')) return;
    setBusy(true); setMsg(null);
    try {
      const r = await api.importCollection(name, file, mode);
      const extra = [r.skipped_duplicates ? `${plural(r.skipped_duplicates, 'duplicate')} skipped` : '', r.rows_without_name.length ? `rows without a name skipped: ${r.rows_without_name.join(', ')}` : ''].filter(Boolean).join(' · ');
      setMsg({ ok: true, text: `Added ${plural(r.added, 'leader')}. List now has ${r.total}.${extra ? ` ${extra}.` : ''}` });
      if (mode === 'replace') setMode('append');
      onDone();
    } catch (err) {
      setMsg({ ok: false, text: err.response?.data?.detail || 'Import failed. Check the file and try again.' });
    } finally { setBusy(false); }
  };
  return (
    <div className="bg-white rounded-xl border border-neutral-200 p-5 mb-5" data-testid="bulk-import">
      <p className="font-medium">Bulk upload from Excel or CSV</p>
      <p className="text-sm text-neutral-500 mt-1">First row must be headers: <b>Name</b>, Designation, Organisation, Sector, Citation. Only Name is required. Photos can be added afterwards by editing a leader.</p>
      <div className="flex flex-wrap items-center gap-3 mt-4">
        <select value={mode} onChange={(e) => setMode(e.target.value)} className="border border-neutral-300 rounded-lg px-3 py-2 text-sm" data-testid="bulk-import-mode">
          <option value="append">Add to the current list</option>
          <option value="replace">Replace the whole list</option>
        </select>
        <button onClick={() => ref.current?.click()} disabled={busy} className="inline-flex items-center gap-1.5 bg-[#111111] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#D01919] transition-colors disabled:opacity-50" data-testid="bulk-import-button">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} {busy ? 'Importing…' : 'Choose file (.xlsx / .csv)'}
        </button>
        <button onClick={downloadTemplate} className="inline-flex items-center gap-1.5 border border-neutral-300 px-4 py-2 rounded-lg text-sm hover:bg-neutral-50" data-testid="bulk-import-template"><Download size={16} /> Download template</button>
        <input ref={ref} type="file" accept=".xlsx,.xlsm,.csv" className="hidden" onChange={onFile} data-testid="bulk-import-file" />
      </div>
      {msg ? <p className={`text-sm mt-3 ${msg.ok ? 'text-green-700' : 'text-[#D01919]'}`} data-testid="bulk-import-result">{msg.text}</p> : null}
    </div>
  );
}
